import {
  AgGridReact,
  type CustomCellEditorProps,
} from '@mint-lib/ag-grid/react';
import { useCallback, useMemo } from 'react';

import {
  CellStyleModule,
  ClientSideRowModelModule,
  ColDef,
  CustomEditorModule,
  ModuleRegistry,
  PinnedRowModule,
  RenderApiModule,
  TextEditorModule,
  ValidationModule,
  type GetRowIdParams,
  type NewValueParams,
  type ValueGetterParams,
  type ValueSetterParams,
} from '@mint-lib/ag-grid/community';
import {
  BudgetEditor,
  DateRangeEditor,
  TextEditor,
} from '@mint-lib/ag-grid/editors';
import {
  LicenseManager,
  RowGroupingModule,
} from '@mint-lib/ag-grid/enterprise';
import {
  colDefWithErrorHandling,
  createContext,
} from '@mint-lib/ag-grid/factory';
import { useTranslation } from '@mint-lib/i18n';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import type { DateRange, Matcher } from '@mint-lib/tw-components/patterns';
import type { MediaPlan } from '@mint-sdk/api-media-plan';
import { format, parseISO } from 'date-fns';

import { useMediaPlan } from '../../../../../../api/queries/useMediaPlan.js';
import { FlightBudgetRenderer } from '../../../../../../shared/ColumnFields/ReadOnly/flights/FlightBudgetRenderer.jsx';
import FlightDateRangeRenderer from '../../../../../../shared/ColumnFields/ReadOnly/flights/FlightDateRangeRenderer.jsx';
import FlightTextRenderer from '../../../../../../shared/ColumnFields/ReadOnly/flights/FlightTextRenderer.jsx';
import {
  FLIGHT_TOTAL_ROW_ID,
  type FlightItem,
} from '../../../../../../types/flights.types.js';

LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_LICENSE_KEY);

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  RowGroupingModule,
  ValidationModule,
  PinnedRowModule,
  CellStyleModule,
  TextEditorModule,
  CustomEditorModule,
  RenderApiModule,
]);

type Props = {
  flightsData?: MediaPlan.FlightsList['data'];
  isLoading: boolean;
  handleCellUpdate: (params: NewValueParams<FlightItem>) => void;
  handleDateCellUpdate: (params: NewValueParams<FlightItem>) => void;
  flightsGridRef: React.RefObject<AgGridReact<FlightItem> | null>;
  lockedFlightIds: Set<string>;
};

const ISO_DATE_FORMAT = 'yyyy-MM-dd';

const FlightsTable: React.FC<Props> = ({
  flightsData,
  isLoading,
  handleCellUpdate,
  handleDateCellUpdate,
  flightsGridRef,
  lockedFlightIds,
}) => {
  const { t } = useTranslation('@myn/planning_v2');

  const isReadOnly = !useIsAllowedPermission('campaignPlanningFeature:modify');

  const { budget, dateFrom, dateTo } = flightsData?.additional?.totals ?? {};

  const { data: mediaPlan } = useMediaPlan();
  const currency = mediaPlan?.currency;

  const columnsDefs: ColDef<FlightItem>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('Flight'),
        editable: (params) => {
          return !isReadOnly && params?.data?.id !== FLIGHT_TOTAL_ROW_ID;
        },
        cellRenderer: FlightTextRenderer,
        cellEditor: TextEditor,
        onCellValueChanged: handleCellUpdate,
      },
      colDefWithErrorHandling<FlightItem, DateRange>({
        colId: 'dates',
        headerName: t('Start and End Date'),
        editable: (params) => {
          return (
            !isReadOnly &&
            params?.data?.id !== FLIGHT_TOTAL_ROW_ID &&
            !lockedFlightIds.has(params?.data?.id ?? '')
          );
        },
        tooltip: (params) => {
          if (lockedFlightIds.has(params.data?.id ?? '')) {
            return t('Flight is pending approval. Dates cannot be edited.');
          }
        },
        valueGetter: (params: ValueGetterParams<FlightItem>) => {
          const { dateFrom, dateTo } = params.data ?? {};
          return {
            from: dateFrom ? parseISO(dateFrom) : undefined,
            to: dateTo ? parseISO(dateTo) : undefined,
          };
        },
        valueSetter: (params: ValueSetterParams<FlightItem, DateRange>) => {
          const { from, to } = params.newValue ?? {};

          if (!from || !to) {
            return false;
          }

          params.data.dateFrom = format(from, ISO_DATE_FORMAT);
          params.data.dateTo = format(to, ISO_DATE_FORMAT);

          return true;
        },
        cellRenderer: FlightDateRangeRenderer,
        cellEditor: DateRangeEditor,
        cellEditorParams: (
          params: CustomCellEditorProps<FlightItem, DateRange>,
        ) => {
          const { minDate, maxDate } = params.data;

          return {
            ...params,
            calendarRangeProps: {
              disabledDates: {
                before: parseISO(minDate),
                after: parseISO(maxDate),
              } satisfies Matcher,
            },
          };
        },
        onCellValueChanged: handleDateCellUpdate,
        cellEditorPopup: true,
        cellEditorPopupPosition: 'under',
      }),
      colDefWithErrorHandling({
        field: 'budget',
        valueGetter: (params) => params.data?.budget,
        headerName: t('Budget'),
        editable: (params) => {
          return (
            !isReadOnly &&
            params?.data?.id !== FLIGHT_TOTAL_ROW_ID &&
            !lockedFlightIds.has(params?.data?.id ?? '')
          );
        },
        tooltip: (params) => {
          if (lockedFlightIds.has(params.data?.id ?? '')) {
            return t('Flight is pending approval. Budget cannot be edited.');
          }
        },
        cellRenderer: FlightBudgetRenderer,
        cellRendererParams: { currency },
        cellEditor: BudgetEditor,
        onCellValueChanged: handleCellUpdate,
        cellEditorParams: {
          currency,
          InputProps: {
            restFormatProps: {
              min: 0,
            },
          },
        },
      }),
    ],
    [
      currency,
      handleCellUpdate,
      handleDateCellUpdate,
      t,
      isReadOnly,
      lockedFlightIds,
    ],
  );

  const pinnedTopRowData = [
    {
      name: t('Total'),
      budget,
      dateFrom,
      dateTo,
      id: FLIGHT_TOTAL_ROW_ID,
    },
  ];

  const defaultColDefs = useMemo<ColDef>(() => {
    return {
      // need to set this param to false to prevent the grid from inferring the type of the cell data
      // read more:https://www.ag-grid.com/react-data-grid/cell-data-types/#inferring-data-types
      cellDataType: false,
      flex: 1,
      cellClassRules: {
        'bg-ui-secondary font-semibold !border-none': (params) =>
          !!params.node.rowPinned,
      },
    };
  }, []);

  // this function is mandatory in order to update the grid correctly
  const getRowId = useCallback((params: GetRowIdParams<FlightItem>) => {
    return params.data.id;
  }, []);

  const context = useMemo(() => createContext(), []);

  return (
    <AgGridReact
      ref={flightsGridRef}
      getRowId={getRowId}
      columnDefs={columnsDefs}
      rowData={flightsData?.items}
      defaultColDef={defaultColDefs}
      pinnedTopRowData={pinnedTopRowData}
      loading={isLoading}
      stopEditingWhenCellsLoseFocus
      tooltipShowDelay={0}
      context={context}
    />
  );
};

export default FlightsTable;
